import type { MarkdownTree } from '@app/formats'
import { TRANSCRIPT_MARKDOWN_PARSE_MS } from './transcript-markdown-bounds'

type WorkerReply =
  | { id: number; ok: true; tree: MarkdownTree }
  | { id: number; ok: false }

type Pending = {
  resolve: (tree: MarkdownTree | null) => void
  timer: ReturnType<typeof setTimeout>
}

type MarkdownWorker = Pick<
  Worker,
  'onmessage' | 'onerror' | 'postMessage' | 'terminate'
>

type WorkerFactory = () => MarkdownWorker

let nextId = 0
const pending = new Map<number, Pending>()

/**
 * Parse one stored reply in a worker of its own.
 *
 * A worker is intentionally per reply: ending a parse that has spent its time
 * must not discard another turn that happened to be queued behind it. The
 * worker is also ended after success, so a transcript leaves none behind.
 */
export function parseTranscriptMarkdown(
  body: string,
  makeWorker: WorkerFactory = () =>
    new Worker(new URL('./transcript-markdown.worker.ts', import.meta.url), {
      type: 'module',
    }),
  timeoutMs = TRANSCRIPT_MARKDOWN_PARSE_MS,
): Promise<MarkdownTree | null> {
  const id = ++nextId
  const worker = makeWorker()

  return new Promise((resolve) => {
    const finish = (tree: MarkdownTree | null) => {
      const request = pending.get(id)
      if (!request) return
      clearTimeout(request.timer)
      pending.delete(id)
      worker.terminate()
      request.resolve(tree)
    }
    const timer = setTimeout(() => finish(null), timeoutMs)
    pending.set(id, { resolve, timer })
    worker.onmessage = ({ data }: MessageEvent<WorkerReply>) => {
      if (data.id !== id) return
      finish(data.ok ? data.tree : null)
    }
    worker.onerror = () => finish(null)
    worker.postMessage({ id, body })
  })
}
